import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faShieldCheck, faTimer, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';

import './ForumCertificationModal.scss';
import Drawer from '../Drawer/Drawer';
import Button, { BUTTON_VARIANTS } from '../UI/Button/Button';
import Group from '../UI/Group/Group';
import createLink from '../../../libs/createLink';
import useCurrentUser from '../../../hooks/useCurrentUser';
import {
    fetchCertificationStatus,
    USER_CURRENT_CERTIFICATION_STATUS_KEY,
} from '../../../api/public-voissa/users';

// Modale du forum réservé aux profils certifiés : elle s'ouvre à chaque tentative de créer un sujet
// ou de répondre, tant que le profil n'est pas certifié. La sortie proposée est la certification —
// gratuite — et surtout pas un abonnement : payer ne serait pas une réponse honnête à « votre
// profil n'est pas identifié », et c'est bien la certification qu'on cherche à faire décoller.
//
// Enregistrée en modale GLOBALE (modalTypes, cf. src/index.js) : elle est déclenchée depuis
// plusieurs pages du forum et le filet de sécurité (échec d'une mutation) doit pouvoir l'ouvrir
// sans connaître le composant appelant.
const ForumCertificationModal = ({ handleClose = () => {} }) => {
    const navigate = useNavigate();
    const currentUser = useCurrentUser();

    // Une demande déjà déposée change tout le discours : inviter à « se certifier » quelqu'un dont
    // le dossier est en cours d'examen est le meilleur moyen de le faire recommencer pour rien.
    // La query est celle de la page certification (staleTime 5 min) : souvent déjà en cache.
    const { data: certificationStatus } = useQuery(
        [USER_CURRENT_CERTIFICATION_STATUS_KEY],
        () => fetchCertificationStatus(),
        { staleTime: 1000 * 60 * 5, enabled: !!currentUser?._id }
    );

    const isPending = certificationStatus?.['certification-video']?.statusText === 'pending';

    const goCertify = () => {
        handleClose();
        navigate(createLink('userMeSettingsCertification', currentUser));
    };

    return (
        <Drawer small open={true} closeBtn={false} handleClose={handleClose}>
            <div className="forum-certification">
                <div className="forum-certification-header">
                    <span className="forum-certification-icon">
                        <FontAwesomeIcon icon={faComments} />
                    </span>
                    <h2 className="forum-certification-title">
                        Le forum est réservé aux profils certifiés
                    </h2>
                </div>

                <p className="forum-certification-text">
                    Pour créer un sujet ou répondre sur le forum, votre profil doit être certifié. Les
                    discussions y gagnent en qualité et en confiance&nbsp;: chacun sait à qui il s'adresse,
                    et les faux profils n'y ont plus leur place.
                </p>

                {isPending ? (
                    <>
                        <div className="forum-certification-highlight">
                            <FontAwesomeIcon icon={faTimer} className="forum-certification-highlight-icon" />
                            <div>
                                <b>Votre demande de certification est en cours d'examen.</b> Dès qu'elle sera
                                validée, vous pourrez participer au forum.
                            </div>
                        </div>

                        <p className="forum-certification-note">
                            <FontAwesomeIcon icon={faCircleInfo} /> En attendant, vous pouvez continuer à lire
                            les discussions et à réagir aux messages.
                        </p>

                        <Group right>
                            <Button variant={BUTTON_VARIANTS.REVERSE} onClick={goCertify}>
                                Suivre ma demande
                            </Button>
                            <Button onClick={handleClose}>Fermer</Button>
                        </Group>
                    </>
                ) : (
                    <>
                        <div className="forum-certification-highlight">
                            <FontAwesomeIcon icon={faShieldCheck} className="forum-certification-highlight-icon" />
                            <div>
                                <b>La certification est gratuite</b> et prend moins de deux minutes&nbsp;: une
                                courte capture vidéo depuis votre navigateur suffit à confirmer que vous êtes
                                bien la personne de votre profil. Vos images ne sont jamais publiées.
                            </div>
                        </div>

                        <ul className="forum-certification-benefits">
                            <li>Créez des sujets et répondez sur tout le forum</li>
                            <li>Affichez le badge certifié sur votre profil</li>
                            <li>Publiez à toute heure, sans restriction nocturne</li>
                            <li>Rassurez les membres&nbsp;: un profil certifié inspire confiance</li>
                        </ul>

                        <p className="forum-certification-note">
                            <FontAwesomeIcon icon={faCircleInfo} /> Sans certification, vous pouvez toujours
                            lire les discussions et réagir aux messages.
                        </p>

                        <Group right>
                            <Button variant={BUTTON_VARIANTS.REVERSE} onClick={goCertify}>
                                Certifier mon profil
                            </Button>
                            <Button onClick={handleClose}>Plus tard</Button>
                        </Group>
                    </>
                )}
            </div>
        </Drawer>
    );
};

export default ForumCertificationModal;
